import { useEffect, useState } from "react";
import { Loader2, Key, CheckCircle2, AlertTriangle, Cloud, Trash2, Mail, Send, BarChart3, Stethoscope, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import api, { formatApiError } from "@/lib/api";

export default function AdminSettings() {
  const { user } = useAuth();

  // Cloudinary state
  const [cloud, setCloud] = useState({ cloud_name: "", api_key: "", api_secret: "" });
  const [savedInfo, setSavedInfo] = useState(null);
  const [savingCloud, setSavingCloud] = useState(false);

  // Resend state
  const [resend, setResend] = useState({ api_key: "", to_address: "", from_address: "" });
  const [resendInfo, setResendInfo] = useState(null);
  const [savingResend, setSavingResend] = useState(false);
  const [testingResend, setTestingResend] = useState(false);

  // Tracking state (Meta Pixel + GA4)
  const [tracking, setTracking] = useState({ meta_pixel_id: "", ga4_measurement_id: "" });
  const [trackingSaved, setTrackingSaved] = useState({ meta_pixel_id: "", ga4_measurement_id: "" });
  const [savingTracking, setSavingTracking] = useState(false);

  // Cloudinary diagnostic state
  const [diagnostics, setDiagnostics] = useState(null);
  const [runningDiag, setRunningDiag] = useState(false);

  const runDiagnostics = async () => {
    setRunningDiag(true);
    setDiagnostics(null);
    try {
      const { data } = await api.get("/cloudinary/diagnostics");
      setDiagnostics(data);
      if (data?.ok) toast.success("All checks passed.");
      else toast.error("One or more checks failed — see report below.");
    } catch (err) {
      setDiagnostics({ ok: false, checks: [{ step: "unreachable", ok: false, detail: formatApiError(err?.response?.data?.detail, "Backend unreachable.") }] });
      toast.error("Could not run diagnostics.");
    } finally {
      setRunningDiag(false);
    }
  };

  useEffect(() => {
    api.get("/settings/cloudinary")
      .then((r) => {
        setSavedInfo(r.data);
        setCloud((c) => ({ ...c, cloud_name: r.data.cloud_name || "", api_key: r.data.api_key || "" }));
      })
      .catch(() => {});

    api.get("/settings/resend")
      .then((r) => {
        setResendInfo(r.data);
        setResend((s) => ({
          ...s,
          to_address: r.data.to_address || "",
          from_address: r.data.from_address || "",
        }));
      })
      .catch(() => {});

    api.get("/settings/tracking")
      .then((r) => {
        const data = {
          meta_pixel_id: r.data?.meta_pixel_id || "",
          ga4_measurement_id: r.data?.ga4_measurement_id || "",
        };
        setTracking(data);
        setTrackingSaved(data);
      })
      .catch(() => {});
  }, []);

  const saveCloud = async (e) => {
    e.preventDefault();
    if (!cloud.cloud_name || !cloud.api_key || !cloud.api_secret) {
      return toast.error("All three Cloudinary fields are required.");
    }
    setSavingCloud(true);
    try {
      await api.put("/settings/cloudinary", cloud);
      toast.success("Cloudinary connected · Uploads are live.");
      const r = await api.get("/settings/cloudinary");
      setSavedInfo(r.data);
      setCloud((c) => ({ ...c, api_secret: "" }));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Could not verify Cloudinary credentials."));
    } finally {
      setSavingCloud(false);
    }
  };

  const disconnectCloud = async () => {
    if (!window.confirm("Disconnect Cloudinary? Uploads will stop working until you reconnect.")) return;
    try {
      await api.delete("/settings/cloudinary");
      setSavedInfo({ configured: false, cloud_name: "", api_key: "", api_secret_masked: "" });
      setCloud({ cloud_name: "", api_key: "", api_secret: "" });
      toast.success("Cloudinary disconnected.");
    } catch {
      toast.error("Could not disconnect.");
    }
  };

  const saveResend = async (e) => {
    e.preventDefault();
    if (!resend.to_address) return toast.error("Destination email is required.");
    if (!resend.api_key && !resendInfo?.configured) return toast.error("Resend API key is required.");
    setSavingResend(true);
    try {
      const payload = {
        to_address: resend.to_address,
        from_address: resend.from_address || undefined,
        api_key: resend.api_key,
      };
      // If user is just updating to_address/from_address without changing the key,
      // we still need to send a key. Block them with a helpful toast.
      if (!resend.api_key) {
        toast.error("Paste your Resend API key again to update settings.");
        setSavingResend(false);
        return;
      }
      await api.put("/settings/resend", payload);
      toast.success("Email notifications enabled.");
      const r = await api.get("/settings/resend");
      setResendInfo(r.data);
      setResend((s) => ({ ...s, api_key: "" }));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Could not save Resend settings."));
    } finally {
      setSavingResend(false);
    }
  };

  const sendTestEmail = async () => {
    setTestingResend(true);
    try {
      await api.post("/settings/resend/test");
      toast.success(`Test email sent to ${resendInfo?.to_address}. Check your inbox in 30s.`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Could not send test email."));
    } finally {
      setTestingResend(false);
    }
  };

  const disconnectResend = async () => {
    if (!window.confirm("Disconnect Resend? Lead notifications will stop.")) return;
    try {
      await api.delete("/settings/resend");
      setResendInfo({ configured: false, api_key_masked: "", to_address: "", from_address: "" });
      setResend({ api_key: "", to_address: "", from_address: "" });
      toast.success("Resend disconnected.");
    } catch {
      toast.error("Could not disconnect.");
    }
  };

  const saveTracking = async (e) => {
    e.preventDefault();
    const meta = tracking.meta_pixel_id.trim();
    const ga4 = tracking.ga4_measurement_id.trim();
    if (meta && !/^\d+$/.test(meta)) return toast.error("Meta Pixel ID must be numbers only.");
    if (ga4 && !/^G-/.test(ga4)) return toast.error("GA4 Measurement ID must start with 'G-'.");
    setSavingTracking(true);
    try {
      const r = await api.put("/settings/tracking", { meta_pixel_id: meta, ga4_measurement_id: ga4 });
      const next = {
        meta_pixel_id: r.data?.meta_pixel_id || "",
        ga4_measurement_id: r.data?.ga4_measurement_id || "",
      };
      setTrackingSaved(next);
      toast.success(meta || ga4 ? "Tracking pixels saved. Reload the public site to activate." : "Tracking pixels cleared.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Could not save tracking settings."));
    } finally {
      setSavingTracking(false);
    }
  };

  const disconnectTracking = async () => {
    if (!window.confirm("Remove all tracking pixels?")) return;
    try {
      await api.delete("/settings/tracking");
      const empty = { meta_pixel_id: "", ga4_measurement_id: "" };
      setTracking(empty);
      setTrackingSaved(empty);
      toast.success("Tracking pixels removed.");
    } catch {
      toast.error("Could not remove tracking.");
    }
  };

  // Password state
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const submitPw = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters.");
    if (next !== confirm) return toast.error("Passwords don't match.");
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail, "Could not update password"));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div data-testid="admin-settings-page">
      <header className="mb-8">
        <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/50 mb-2">Account</div>
        <h1 className="font-display text-3xl md:text-4xl text-charcoal">Settings</h1>
      </header>

      {/* Cloudinary */}
      <section className="bg-white border border-beige p-6 md:p-8 max-w-2xl mb-8" data-testid="settings-integrations">
        <div className="flex items-center gap-2 text-teal-600 mb-2">
          <Cloud className="h-4 w-4" strokeWidth={1.5} />
          <span className="uppercase tracking-[0.22em] text-[11px] font-medium">Integrations · Cloudinary</span>
        </div>
        <div className="font-display text-xl text-charcoal mb-1">Image & video uploads</div>
        <p className="text-charcoal/65 text-sm mb-5 leading-relaxed">
          Add your Cloudinary credentials to enable direct uploads from admin.
          Get them at <a href="https://cloudinary.com/console" target="_blank" rel="noreferrer" className="text-teal-600 underline">cloudinary.com/console</a>.
        </p>

        <div className={`flex items-center gap-2 px-3 py-2 mb-5 border ${savedInfo?.configured ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="cloudinary-status">
          {savedInfo?.configured
            ? <><CheckCircle2 className="h-4 w-4" /> <span className="text-sm">Connected · cloud <code className="font-mono">{savedInfo.cloud_name}</code></span></>
            : <><AlertTriangle className="h-4 w-4" /> <span className="text-sm">Not connected — uploads will fail until you save credentials.</span></>}
        </div>

        <form onSubmit={saveCloud} className="space-y-5" data-testid="cloudinary-form">
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Cloud name</span>
            <input type="text" value={cloud.cloud_name} onChange={(e) => setCloud((c) => ({ ...c, cloud_name: e.target.value }))} placeholder="e.g. dxxxxxxxx" data-testid="cloudinary-cloud-name" className="mt-2 field-input" required />
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">API key</span>
            <input type="text" value={cloud.api_key} onChange={(e) => setCloud((c) => ({ ...c, api_key: e.target.value }))} data-testid="cloudinary-api-key" className="mt-2 field-input" required />
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">
              API secret {savedInfo?.api_secret_masked ? <span className="ml-2 lowercase tracking-wider text-charcoal/40">currently {savedInfo.api_secret_masked}</span> : null}
            </span>
            <input type="password" value={cloud.api_secret} onChange={(e) => setCloud((c) => ({ ...c, api_secret: e.target.value }))} data-testid="cloudinary-api-secret" placeholder={savedInfo?.api_secret_masked ? "Paste to change" : ""} className="mt-2 field-input" required={!savedInfo?.configured} />
          </label>
          <div className="flex flex-wrap gap-3 pt-2">
            <button type="submit" disabled={savingCloud} data-testid="cloudinary-save" className="btn-primary h-11 px-6 text-[11px] disabled:opacity-60">
              {savingCloud ? <><Loader2 className="h-4 w-4 animate-spin" />Verifying&hellip;</> : "Verify & save"}
            </button>
            {savedInfo?.configured ? (
              <button type="button" onClick={runDiagnostics} disabled={runningDiag} data-testid="cloudinary-diagnose" className="inline-flex items-center gap-2 h-11 px-4 border border-beige text-charcoal text-[11px] uppercase tracking-[0.22em] hover:border-teal-600 hover:text-teal-600 disabled:opacity-60">
                {runningDiag ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Stethoscope className="h-3.5 w-3.5" />} Run diagnostics
              </button>
            ) : null}
            {savedInfo?.configured ? (
              <button type="button" onClick={disconnectCloud} data-testid="cloudinary-disconnect" className="inline-flex items-center gap-2 h-11 px-4 border border-rose-300 text-rose-700 text-[11px] uppercase tracking-[0.22em] hover:bg-rose-700 hover:text-ivory">
                <Trash2 className="h-3.5 w-3.5" /> Disconnect
              </button>
            ) : null}
          </div>

          {diagnostics ? (
            <div className="mt-5 border border-beige p-4 bg-sage/15" data-testid="cloudinary-diagnostics-report">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {diagnostics.ok
                    ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /><span className="text-sm font-medium text-emerald-800">All checks passed</span></>
                    : <><AlertTriangle className="h-4 w-4 text-rose-700" /><span className="text-sm font-medium text-rose-800">One or more checks failed</span></>}
                </div>
                <button type="button" onClick={() => setDiagnostics(null)} className="text-charcoal/45 hover:text-charcoal"><XIcon className="h-3.5 w-3.5" /></button>
              </div>
              <ul className="space-y-2">
                {(diagnostics.checks || []).map((c, i) => (
                  <li key={i} className="text-xs flex items-start gap-2 border-b border-beige/70 pb-2 last:border-b-0" data-testid={`diag-${c.step}`}>
                    <span className={`inline-block w-1.5 h-1.5 mt-1.5 ${c.ok ? "bg-emerald-600" : "bg-rose-600"}`} />
                    <div className="flex-1">
                      <div className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55 mb-0.5">{(c.step || "").replace(/_/g, " ")}</div>
                      <div className="text-charcoal/80 break-all">{c.detail}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </form>
      </section>

      {/* Resend */}
      <section className="bg-white border border-beige p-6 md:p-8 max-w-2xl mb-8" data-testid="settings-notifications">
        <div className="flex items-center gap-2 text-teal-600 mb-2">
          <Mail className="h-4 w-4" strokeWidth={1.5} />
          <span className="uppercase tracking-[0.22em] text-[11px] font-medium">Notifications · Resend</span>
        </div>
        <div className="font-display text-xl text-charcoal mb-1">New lead emails</div>
        <p className="text-charcoal/65 text-sm mb-5 leading-relaxed">
          Get a branded email the moment a couple submits the enquiry form. Get an API key (free, 3,000/mo) at{" "}
          <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-teal-600 underline">resend.com/api-keys</a>.
        </p>

        <div className={`flex items-center gap-2 px-3 py-2 mb-5 border ${resendInfo?.configured ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="resend-status">
          {resendInfo?.configured
            ? <><CheckCircle2 className="h-4 w-4" /> <span className="text-sm">Connected · sending to <code className="font-mono">{resendInfo.to_address}</code></span></>
            : <><AlertTriangle className="h-4 w-4" /> <span className="text-sm">Not connected — new leads are saved but no email is sent.</span></>}
        </div>

        <form onSubmit={saveResend} className="space-y-5" data-testid="resend-form">
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">
              API key {resendInfo?.api_key_masked ? <span className="ml-2 lowercase tracking-wider text-charcoal/40">currently {resendInfo.api_key_masked}</span> : null}
            </span>
            <input
              type="password"
              value={resend.api_key}
              onChange={(e) => setResend((s) => ({ ...s, api_key: e.target.value }))}
              data-testid="resend-api-key"
              placeholder={resendInfo?.api_key_masked ? "Paste to change" : "re_xxxxxxxxxx"}
              className="mt-2 field-input"
              required={!resendInfo?.configured}
            />
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Send notifications to</span>
            <input
              type="email"
              value={resend.to_address}
              onChange={(e) => setResend((s) => ({ ...s, to_address: e.target.value }))}
              data-testid="resend-to-address"
              placeholder="hello@weddingsbymostash.com"
              className="mt-2 field-input"
              required
            />
            <span className="text-[11px] text-charcoal/45 mt-1 block">Multiple addresses? Separate with commas.</span>
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">From address (optional)</span>
            <input
              type="text"
              value={resend.from_address}
              onChange={(e) => setResend((s) => ({ ...s, from_address: e.target.value }))}
              data-testid="resend-from-address"
              placeholder="Weddings by Mostash <onboarding@resend.dev>"
              className="mt-2 field-input"
            />
            <span className="text-[11px] text-charcoal/45 mt-1 block">
              Leave blank to use Resend&apos;s sandbox. Verify your domain at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="text-teal-600 underline">resend.com/domains</a> to send from your own email.
            </span>
          </label>
          <div className="flex flex-wrap gap-3 pt-2">
            <button type="submit" disabled={savingResend} data-testid="resend-save" className="btn-primary h-11 px-6 text-[11px] disabled:opacity-60">
              {savingResend ? <><Loader2 className="h-4 w-4 animate-spin" />Verifying…</> : "Verify & save"}
            </button>
            {resendInfo?.configured ? (
              <>
                <button type="button" onClick={sendTestEmail} disabled={testingResend} data-testid="resend-test" className="inline-flex items-center gap-2 h-11 px-4 border border-teal-600 text-teal-600 text-[11px] uppercase tracking-[0.22em] hover:bg-teal-600 hover:text-ivory disabled:opacity-60">
                  {testingResend ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                  Send test email
                </button>
                <button type="button" onClick={disconnectResend} data-testid="resend-disconnect" className="inline-flex items-center gap-2 h-11 px-4 border border-rose-300 text-rose-700 text-[11px] uppercase tracking-[0.22em] hover:bg-rose-700 hover:text-ivory">
                  <Trash2 className="h-3.5 w-3.5" /> Disconnect
                </button>
              </>
            ) : null}
          </div>
        </form>
      </section>

      {/* Tracking · Meta Pixel + GA4 */}
      <section className="bg-white border border-beige p-6 md:p-8 max-w-2xl mb-8" data-testid="settings-tracking">
        <div className="flex items-center gap-2 text-teal-600 mb-2">
          <BarChart3 className="h-4 w-4" strokeWidth={1.5} />
          <span className="uppercase tracking-[0.22em] text-[11px] font-medium">Analytics · Pixels & GA4</span>
        </div>
        <div className="font-display text-xl text-charcoal mb-1">Track visitors & lead submissions</div>
        <p className="text-charcoal/65 text-sm mb-5 leading-relaxed">
          Paste your Meta Pixel ID and Google Analytics 4 Measurement ID. We&apos;ll auto-load the scripts on every public page and
          fire a <code className="font-mono">Lead</code> / <code className="font-mono">generate_lead</code> event when a couple
          submits the enquiry form.
        </p>

        <div className={`flex items-center gap-2 px-3 py-2 mb-5 border ${(trackingSaved.meta_pixel_id || trackingSaved.ga4_measurement_id) ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`} data-testid="tracking-status">
          {(trackingSaved.meta_pixel_id || trackingSaved.ga4_measurement_id) ? (
            <>
              <CheckCircle2 className="h-4 w-4" />
              <span className="text-sm">
                Active ·{" "}
                {trackingSaved.meta_pixel_id ? <>Meta <code className="font-mono">{trackingSaved.meta_pixel_id}</code></> : null}
                {trackingSaved.meta_pixel_id && trackingSaved.ga4_measurement_id ? " · " : null}
                {trackingSaved.ga4_measurement_id ? <>GA4 <code className="font-mono">{trackingSaved.ga4_measurement_id}</code></> : null}
              </span>
            </>
          ) : (
            <>
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm">No pixels configured — ad attribution and analytics are inactive.</span>
            </>
          )}
        </div>

        <form onSubmit={saveTracking} className="space-y-5" data-testid="tracking-form">
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Meta Pixel ID</span>
            <input
              type="text"
              value={tracking.meta_pixel_id}
              onChange={(e) => setTracking((s) => ({ ...s, meta_pixel_id: e.target.value }))}
              data-testid="tracking-meta-pixel-id"
              placeholder="e.g. 1234567890123456"
              className="mt-2 field-input"
            />
            <span className="text-[11px] text-charcoal/45 mt-1 block">
              Get it at <a href="https://business.facebook.com/events_manager" target="_blank" rel="noreferrer" className="text-teal-600 underline">Meta Events Manager</a> → Pixel → Settings.
            </span>
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">GA4 Measurement ID</span>
            <input
              type="text"
              value={tracking.ga4_measurement_id}
              onChange={(e) => setTracking((s) => ({ ...s, ga4_measurement_id: e.target.value }))}
              data-testid="tracking-ga4-id"
              placeholder="G-XXXXXXXXXX"
              className="mt-2 field-input"
            />
            <span className="text-[11px] text-charcoal/45 mt-1 block">
              Find it at <a href="https://analytics.google.com/" target="_blank" rel="noreferrer" className="text-teal-600 underline">analytics.google.com</a> → Admin → Data Streams → Web stream.
            </span>
          </label>
          <div className="flex flex-wrap gap-3 pt-2">
            <button type="submit" disabled={savingTracking} data-testid="tracking-save" className="btn-primary h-11 px-6 text-[11px] disabled:opacity-60">
              {savingTracking ? <><Loader2 className="h-4 w-4 animate-spin" />Saving…</> : "Save tracking IDs"}
            </button>
            {(trackingSaved.meta_pixel_id || trackingSaved.ga4_measurement_id) ? (
              <button type="button" onClick={disconnectTracking} data-testid="tracking-disconnect" className="inline-flex items-center gap-2 h-11 px-4 border border-rose-300 text-rose-700 text-[11px] uppercase tracking-[0.22em] hover:bg-rose-700 hover:text-ivory">
                <Trash2 className="h-3.5 w-3.5" /> Remove pixels
              </button>
            ) : null}
          </div>
        </form>
      </section>

      {/* Profile */}
      <section className="bg-white border border-beige p-6 md:p-8 max-w-2xl mb-8">
        <div className="font-display text-xl text-charcoal mb-1">Profile</div>
        <p className="text-charcoal/60 text-sm">Signed in as <span className="text-charcoal font-medium">{user?.email}</span></p>
      </section>

      {/* Password */}
      <section className="bg-white border border-beige p-6 md:p-8 max-w-2xl">
        <div className="flex items-center gap-2 text-teal-600 mb-4">
          <Key className="h-4 w-4" strokeWidth={1.5} />
          <span className="uppercase tracking-[0.22em] text-[11px]">Change password</span>
        </div>
        <form onSubmit={submitPw} className="space-y-5" data-testid="admin-change-password-form">
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Current password</span>
            <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="pw-current" className="mt-2 field-input" />
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">New password</span>
            <input type="password" required value={next} onChange={(e) => setNext(e.target.value)} data-testid="pw-new" className="mt-2 field-input" />
          </label>
          <label className="block">
            <span className="uppercase tracking-[0.22em] text-[10px] text-charcoal/55">Confirm new password</span>
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="pw-confirm" className="mt-2 field-input" />
          </label>
          <button type="submit" disabled={savingPw} data-testid="pw-submit" className="btn-primary h-11 px-6 text-[11px] disabled:opacity-60">
            {savingPw ? <><Loader2 className="h-4 w-4 animate-spin" />Saving</> : "Update password"}
          </button>
        </form>
      </section>
    </div>
  );
}
